import { useState, type ReactNode } from "react";
import { resolvePersonaSkillKeys } from "../../../chat/pills/outlookPills";
import type { CalEvent } from "../types";
import { withoutTeamsBlock, type InviteContent } from "../domain/agenda";
import { agendaChange, type CalendarChange } from "../domain/changes";
import { displaySubject } from "../domain/day";
import { fd } from "../domain/time";
import { useAgendaDraft, type AgendaDraftState } from "../hooks/useAgendaDraft";
import { SparkIcon } from "./CalendarIcons";
import styles from "./CalendarModule.module.css";

/** Why Draft With AiVA is greyed out: the persona doesn't have the calendar skill (its slash command is /meeting). */
const NO_CALENDAR_SKILL = "Draft With AiVA needs the /meeting skill, which your AiVA persona doesn't have. Ask your AiVA admin to add it.";

/** The invite's own text from the short preview, without the Teams join details. */
export function previewText(e: CalEvent): string {
  return withoutTeamsBlock(e.bodyPreview);
}

/** None while AiVA drafts, or after a draft failed: "let AiVA draft it" there read as if nothing had happened. */
function agendaPlaceholder(status: AgendaDraftState["status"]): string {
  return status === "idle" ? "No agenda yet. Type one, or let AiVA draft it from the email thread" : "";
}

/** The agenda as saved: a pending save first, then the invite's agenda block. */
export function savedAgenda(e: CalEvent, invite: InviteContent | null): string {
  return e.pendingAgenda ?? invite?.agenda ?? "";
}

interface MeetingAgendaProps {
  readonly event: CalEvent;
  /** The full invite, split into agenda block and own text; null while loading or if it couldn't be read. */
  readonly invite: InviteContent | null;
  readonly inviteFailed: boolean;
  /** Organiser with write permission: the agenda can be edited and saved. */
  readonly canEdit: boolean;
  /** Your name, so AiVA doesn't guess one for the agenda. */
  readonly userName: string;
  readonly onStage: (change: CalendarChange) => void;
}

/**
 * The Agenda section. The organiser edits the invite's agenda block and saves
 * it explicitly; everyone else reads it. The block is kept apart from the rest
 * of the invite, so the Teams join details survive a save.
 *
 * "Draft With AiVA" puts an AI-written agenda in the box, unsaved: AiVA drafts
 * it from the meeting's email thread (or the invite), and the organiser edits
 * it and saves it like any other.
 */
export function MeetingAgenda({ event, invite, inviteFailed, canEdit, userName, onStage }: Readonly<MeetingAgendaProps>) {
  const [draft, setDraft] = useState<string | null>(null);
  /** Said under the box once AiVA has drafted: where from, and what to do next. */
  const [aivaNote, setAivaNote] = useState<string | null>(null);
  const ai = useAgendaDraft();
  const saved = savedAgenda(event, invite);
  const loading = invite === null && !inviteFailed;
  const editing = canEdit && invite !== null;
  const streaming = ai.state.status === "drafting" ? ai.state : null;
  const drafting = streaming !== null;
  /** Said above the box: under it, the note sat below the fold of the narrow pane, and a failed draft looked like nothing happened. */
  const failed = ai.state.status === "failed" ? ai.state : null;
  const text = streaming ? streaming.text : (draft ?? saved);
  const dirty = !drafting && draft !== null && draft.trim() !== saved.trim();

  // Offered to the organiser; greyed out, with the reason, when the persona lacks the calendar skill.
  // When the persona's skills aren't known yet, the backend decides.
  const personaSkills = resolvePersonaSkillKeys("outlook");
  const aivaAllowed = !personaSkills || personaSkills.has("calendar_assistant");

  const draftWithAiva = async () => {
    setAivaNote(null);
    const people = event.attendees.filter((a) => a.type !== "resource");
    const minutes = Math.round((event.end.getTime() - event.start.getTime()) / 60000);
    const result = await ai.start({
      subject: displaySubject(event),
      durationLabel: fd(minutes),
      location: event.location,
      organizerName: event.isOrganizer ? userName : event.organizerName,
      organizerEmail: event.organizerEmail,
      attendeeEmails: people.map((a) => a.email),
      attendeeNames: people.map((a) => a.name),
      inviteText: invite?.text ?? previewText(event),
      // Whatever is in the box now (typed, or a previous draft) is built on, not thrown away.
      notes: text,
      userDisplayName: userName,
    });
    if (!result) return;
    setDraft(result.text);
    setAivaNote(
      result.savedByAiva
        ? `AiVA drafted this from ${result.source} and also saved it to the invite.`
        : `AiVA drafted this from ${result.source}. Check it and edit it, then Save Agenda. Nothing is saved yet.`,
    );
  };

  let content: ReactNode;
  if (editing) {
    content = (
      <>
        {failed && (
          <span className={`${styles.hint} ${styles.busyNote}`} role="alert">
            {failed.message}
          </span>
        )}
        <textarea
          className={styles.agendaInput}
          value={text}
          readOnly={drafting}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={agendaPlaceholder(ai.state.status)}
          aria-label="Agenda"
          aria-busy={drafting}
        />
        {streaming && <span className={styles.hint}>AiVA is drafting from {streaming.source}…</span>}
        {!drafting && aivaNote && <span className={styles.hint}>{aivaNote}</span>}
        {!aivaAllowed && <span className={styles.hint}>{NO_CALENDAR_SKILL}</span>}
        <div className={styles.agendaActions}>
          <button type="button" className={styles.btn} disabled={!dirty} onClick={() => onStage(agendaChange(event, text))}>
            Save Agenda
          </button>
        </div>
        {invite.text && (
          <div className={styles.agendaAlso}>
            <span className={styles.agendaAlsoLabel}>Also in the invite</span>
            {invite.text}
          </div>
        )}
      </>
    );
  } else if (loading) {
    content = <div className={styles.skel} />;
  } else {
    const shown = saved || invite?.text || (inviteFailed ? previewText(event) : "");
    content = (
      <>
        <div className={styles.agenda}>{shown || <span className={styles.agendaEmpty}>No agenda in this invite.</span>}</div>
        {canEdit && inviteFailed && (
          <span className={styles.hint}>The invite couldn't be read, so its agenda can't be edited here. Open it in Outlook.</span>
        )}
      </>
    );
  }

  let draftLabel = text.trim() ? "Redraft" : "Draft With AiVA";
  if (drafting) draftLabel = "Drafting…";

  return (
    <div className={styles.sec}>
      <div className={styles.secH}>
        <span className={styles.secBar} />
        <span className={styles.secTitle}>Agenda</span>
        {editing && (
          <span className={styles.secRight}>
            <button
              type="button"
              className={styles.newLink}
              onClick={() => void draftWithAiva()}
              disabled={drafting || !aivaAllowed}
              title={aivaAllowed ? undefined : NO_CALENDAR_SKILL}
            >
              <SparkIcon />
              {draftLabel}
            </button>
          </span>
        )}
      </div>
      {content}
    </div>
  );
}
